import TaxiOfficerEditViewBasicProps from "../common/taxi-officer-edit-view-basic-props";
import { FC, useEffect, useState } from "react";
import { useAppContext } from "../../../../../context/app.context";
import { useNavigate } from "react-router-dom";
import useAbort from "../../../../../common/hooks/use-abort";
import useForm from "../../../../../common/components/form/use-form";
import TaxiOfficerEditByTaxiFormData from "./common/types/taxi-officer-edit-by-taxi-form-data";
import taxiOfficerEditByTaxiFormHelper from "./common/taxi-officer-edit-by-taxi-form.helper";
import userRoutesHelper from "../../../common/routes/user-routes.helper";
import userTranslationsHelper from "../../../../../languages/user-translations.helper";
import notificationService from "../../../../../common/utils/notification/notification.service";
import TaxiOfficerEditParams from "../../../../../common/services/taxi-officer/edit/taxi-officer-edit-params";
import taxiOfficerEditByTaxiParamsFactory from "./common/taxi-officer-edit-by-taxi-params.factory";
import taxiOfficerService from "../../../../../common/services/taxi-officer/taxi-officer.service";
import HeadingComponent from "../../../../../common/components/heading/heading.component";
import Row from "../../../../../common/components/grid/row";
import Column from "../../../../../common/components/grid/column";
import CardComponent from "../../../../../common/components/card/card.component";
import FormFieldComponent from "../../../../../common/components/form/field/form-field.component";
import InputComponent from "../../../../../common/components/form/input/input.component";
import PhoneNumberInputComponent from "../../../../../common/components/form/input/phone-number/phone-number-input.component";
import ButtonComponent from "../../../../../common/components/button/button.component";
import useTaxiOfficerDetails from "../../../../../common/services/taxi-officer/details/use-taxi-officer-details";
import taxiOfficerEditByTaxiFormDataFactory from "./common/taxi-officer-edit-by-taxi-form-data.factory";

type TaxiOfficerEditByTaxiProps = TaxiOfficerEditViewBasicProps & {
  taxiOfficerUuid: string;
};

const TaxiOfficerEditByTaxiComponent: FC<TaxiOfficerEditByTaxiProps> = (
  props,
) => {
  const [isTaxiOfficerEditPending, setIsTaxiOfficerEditPending] =
    useState(false);

  const { user } = useAppContext();
  const navigate = useNavigate();
  const taxiOfficerEditAbort = useAbort();

  const form = useForm<TaxiOfficerEditByTaxiFormData>({
    emptyValues: taxiOfficerEditByTaxiFormHelper.getDefaultFormData(),
    validationDefinition:
      taxiOfficerEditByTaxiFormHelper.getValidationDefinition(),
  });

  const taxiOfficerDetails = useTaxiOfficerDetails();

  const taxiOfficerDetailsAbort = useAbort();

  useEffect(() => {
    if (props.taxiOfficerUuid) {
      taxiOfficerDetails.load(
        { taxiOfficerUuid: props.taxiOfficerUuid },
        taxiOfficerDetailsAbort.signal,
      );
    }
  }, [props.taxiOfficerUuid]);

  useEffect(() => {
    if (!taxiOfficerDetails.data) {
      return;
    }

    const formData = taxiOfficerEditByTaxiFormDataFactory.create(
      taxiOfficerDetails.data,
    );

    form.setValues(formData);
  }, [taxiOfficerDetails.data]);

  const navigateToListing = () => {
    const taxiOfficerListingRoute =
      userRoutesHelper.getTaxiOfficerListingRoute();
    navigate(taxiOfficerListingRoute);
  };

  const translations = userTranslationsHelper.getTaxiOfficerEditTranslations();

  const onTaxiOfficerEditSuccess = () => {
    notificationService.success(translations.successEditNotificationLabel);
    navigateToListing();
  };

  const onTaxiOfficerEditFailure = () => {
    notificationService.error(translations.failureEditNotificationLabel);
  };

  const editTaxiOfficer = async () => {
    setIsTaxiOfficerEditPending(true);

    const params: TaxiOfficerEditParams =
      taxiOfficerEditByTaxiParamsFactory.create(form.values, user!);

    try {
      await taxiOfficerService.edit(
        props.taxiOfficerUuid,
        params,
        taxiOfficerEditAbort.signal,
      );

      onTaxiOfficerEditSuccess();
    } catch {
      onTaxiOfficerEditFailure();
    } finally {
      setIsTaxiOfficerEditPending(false);
    }
  };

  const submitForm = async () => {
    const isFormValid = await form.validateAll();

    if (!isFormValid) return;

    editTaxiOfficer();
  };

  return (
    <>
      <HeadingComponent
        title={translations.header.headingLabel}
        actions={props.changeViewButtons}
      />
      <CardComponent>
        <Row>
          <Column lg={6}>
            <FormFieldComponent
              label={translations.form.emailLabel}
              isRequired
              errorMessage={form.validationResults.email.errorMessage}
            >
              <InputComponent
                placeholder={translations.form.emailPlaceholder}
                value={form.values.email}
                onChange={(value) => form.setValue("email", value)}
                onBlur={() => form.validate("email")}
                hasError={!!form.validationResults.email.errorMessage}
                idForTesting={`email`}
              />
            </FormFieldComponent>
          </Column>
          <Column lg={6}>
            <FormFieldComponent
              label={translations.form.firstNameLabel}
              isRequired
              errorMessage={form.validationResults.firstName.errorMessage}
            >
              <InputComponent
                placeholder={translations.form.firstNamePlaceholder}
                value={form.values.firstName}
                onChange={(value) => form.setValue("firstName", value)}
                onBlur={() => form.validate("firstName")}
                hasError={!!form.validationResults.firstName.errorMessage}
                idForTesting={`first-name`}
              />
            </FormFieldComponent>
          </Column>
        </Row>
        <Row>
          <Column lg={6}>
            <FormFieldComponent
              label={translations.form.lastNameLabel}
              isRequired
              errorMessage={form.validationResults.lastName.errorMessage}
            >
              <InputComponent
                placeholder={translations.form.lastNamePlaceholder}
                value={form.values.lastName}
                onChange={(value) => form.setValue("lastName", value)}
                onBlur={() => form.validate("lastName")}
                hasError={!!form.validationResults.lastName.errorMessage}
                idForTesting={`last-name`}
              />
            </FormFieldComponent>
          </Column>
          <Column lg={6}>
            <FormFieldComponent
              label={translations.form.phoneNumberLabel}
              isRequired
              errorMessage={form.validationResults.phoneNumber.errorMessage}
            >
              <PhoneNumberInputComponent
                placeholder={translations.form.phoneNumberPlaceholder}
                phoneNumber={form.values.phoneNumber}
                onChange={(value) => form.setValue("phoneNumber", value)}
                onBlur={() => form.validate("phoneNumber")}
                hasError={!!form.validationResults.phoneNumber.errorMessage}
                idForTesting={`phone-number`}
              />
            </FormFieldComponent>
          </Column>
        </Row>
        <Row>
          <Column lg={6}>
            <FormFieldComponent
              label={translations.form.alternativePhoneNumberLabel}
              errorMessage={
                form.validationResults.alternativePhoneNumber.errorMessage
              }
            >
              <PhoneNumberInputComponent
                placeholder={
                  translations.form.alternativePhoneNumberPlaceholder
                }
                phoneNumber={form.values.alternativePhoneNumber}
                onChange={(value) =>
                  form.setValue("alternativePhoneNumber", value)
                }
                onBlur={() => form.validate("alternativePhoneNumber")}
                hasError={
                  !!form.validationResults.alternativePhoneNumber.errorMessage
                }
                idForTesting={`alternative-phone-number`}
              />
            </FormFieldComponent>
          </Column>
        </Row>
      </CardComponent>
      <ButtonComponent
        onClick={submitForm}
        type="primary"
        isDisabled={isTaxiOfficerEditPending}
        classNames={{ root: "mt-4" }}
        idForTesting={`submit-button`}
        title={translations.form.submitButtonTitle}
      >
        {translations.form.submitButtonLabel}
      </ButtonComponent>
    </>
  );
};

export default TaxiOfficerEditByTaxiComponent;
